import { controlEventApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  ControlEventListI,
  ControlEventI,
  GetControlEventParamsI,
  OperatorOptionI,
  SponsorForEventI,
  CalendarEventI,
  LiveEventI,
  RecordingI,
} from "@interfaces";
import { AxiosError } from "axios";

const getControlEvents = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetControlEventParamsI): Promise<
  ServiceResponseI<DataI<ControlEventListI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await controlEventApi.get<
      ServiceResponseI<DataI<ControlEventListI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventById = async (
  id: string,
): Promise<ServiceResponseI<ControlEventI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.get<
      ServiceResponseI<ControlEventI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const controlEventDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/* ── apoyos del form ────────────────────────────────────────────────── */

export const getOperatorsByCapability = async (
  code: string,
): Promise<ServiceResponseI<OperatorOptionI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("code", code);

    const { data } = await controlEventApi.get<
      ServiceResponseI<OperatorOptionI[]>
    >("/operators-by-capability", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getControlSponsorsForEvent = async (): Promise<
  ServiceResponseI<SponsorForEventI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.get<
      ServiceResponseI<SponsorForEventI[]>
    >("/sponsors-for-event", { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getControlEventsForCalendar = async (): Promise<
  ServiceResponseI<CalendarEventI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.get<
      ServiceResponseI<CalendarEventI[]>
    >("/for-calendar", { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getControlEventLive = async (
  id: string,
): Promise<ServiceResponseI<LiveEventI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.get<ServiceResponseI<LiveEventI>>(
      `/${id}/live`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getControlEventRecordings = async (
  id: string,
): Promise<ServiceResponseI<RecordingI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await controlEventApi.get<ServiceResponseI<RecordingI[]>>(
      `/${id}/recordings`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const downloadControlEventRecording = async (
  id: string,
  recordingId: string,
  fileName: string,
): Promise<void> => {
  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");

  const { data } = await controlEventApi.get(
    `/${id}/recordings/${recordingId}/download`,
    { headers: { Authorization: validateToken }, responseType: "blob" },
  );
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export const uploadControlEventRecording = async (
  id: string,
  blob: Blob,
  ext = "webm",
  durationMs?: number,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const form = new FormData();
    form.append("file", blob, `recording.${ext}`);
    if (durationMs != null) form.append("durationMs", String(durationMs));

    const { data } = await controlEventApi.post<ServiceResponseI<string>>(
      `/${id}/recording`,
      form,
      {
        headers: {
          Authorization: validateToken,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useControlEvents = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetControlEventParamsI) => {
  const controlEventsQuery = useQuery(
    ["control-events", { pagination, sorting, globalFilter, clientId, status }],
    () =>
      getControlEvents({ pagination, sorting, globalFilter, clientId, status }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { controlEventsQuery };
};